import {AfterViewInit, Component, inject} from '@angular/core';
import {MatIcon} from '@angular/material/icon';
import {KeyValuePipe, Location} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {ReportService} from '@services/report/report.service';
import {PatientImage} from '@interfaces/report';
import {MatDivider} from '@angular/material/divider';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatButton} from '@angular/material/button';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatProgressBar} from '@angular/material/progress-bar';
import {RequestImageComponent} from '@pages/reports-requests/request-detail/request-image/request-image.component';
import {TranslatePipe} from '@ngx-translate/core';
import {MatButtonToggle, MatButtonToggleGroup} from '@angular/material/button-toggle';
import {MatOption, MatSelect} from '@angular/material/select';
import {CommaSpacePipe} from '../../../pipes/comma-space.pipe';
import {LoginService} from '@services/login/login.service';

@Component({
  selector: 'app-request-detail',
  imports: [
    MatIcon,
    MatDivider,
    FormsModule,
    ReactiveFormsModule,
    MatButton,
    KeyValuePipe,
    MatProgressBar,
    RequestImageComponent,
    TranslatePipe,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatSelect,
    MatOption,
    CommaSpacePipe
  ],
  templateUrl: './request-detail.component.html',
  standalone: true,
  styleUrl: './request-detail.component.scss'
})
export class RequestDetailComponent implements AfterViewInit {
  private location = inject(Location);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private reportService = inject(ReportService);
  private fb = inject(FormBuilder);
  private loginService = inject(LoginService);

  private _snackBar = inject(MatSnackBar);

  acnePercentage = ['0', '25', '50', '75', '100'];
  spfOptions = ['SPF30', 'SPF50'];
  levelOptions = ['No', 'Mild', 'Moderate', 'Severe'];

  requestId?: string;
  patientId?: string;
  doctorId?: string;
  patientImages?: PatientImage[];
  surveyResult?: any;
  loadingSurvey = true;
  reportForm: FormGroup = this.fb.group({
    acne: [''],
    acne_percentage: [this.acnePercentage[2]],
    acne_recommendation: [''],
    skin_recommendation: [''],
    extra_notes: [''],
    skin_type: [''],
    wrinkles: [''],
    pigmentation: [''],
    spf: [this.spfOptions[0]],
    pigmentation_details: this.fb.group({
      eye_area: [''],
      malar_area: [''],
      overall_face: [''],
      pigmentation_recommendations: [''],
    }),
    wrinkles_details: this.fb.group({
      eye_area: [''],
      nose_area: [''],
      lips_area: [''],
      forehead_area: [''],
      wrinkles_recommendations: [''],
    })
  });

  ngAfterViewInit() {
    //show loading snackbar
    this._snackBar.open('Loading...', '', {
      duration: -1,
    });
    this.route.paramMap.subscribe(params => {
      this.requestId = params.get('requestId') ?? '';
      this.reportService.requestsObservable$
        .subscribe((requests: any) => {
          const request = requests.find((request: any) => request.id === this.requestId);
          if (!request) {
            return;
          }
          this._snackBar.dismiss();
          this.patientId = request.requested_for.id;
          this.doctorId = request.requested_by.id;
          this.reportService.fetchPatientImage(this.patientId!)
            .subscribe((res: PatientImage[]) => {
              this.patientImages = res;
            });

          this.loadingSurvey = true;
          this.reportService.fetchSurveyForPatient(this.patientId!)
            .subscribe({
              next: (res: any) => {
                if(!res || res.length === 0) {
                  this.loadingSurvey = false;
                  return;
                }
                const data = res[res.length - 1].survey_response;
                this.loadingSurvey = false;
                try {
                  let response;
                  if(!data || !data['response']) {
                    response = data;
                  } else {
                      response = data['response'];
                  }
                  this.surveyResult = typeof response === 'string' ? JSON.parse(response) : response;
                } catch (error) {
                  this.surveyResult = data;
                }
                console.log('survey: ' + this.surveyResult);
              },
              error: (err: any) => {
                this.loadingSurvey = false;
              }
            });
        });

    });
  }

  objectKeys = Object.keys;

  isArray(value: any): boolean {
    return Array.isArray(value);
  }


  goBack() {
    this.location.back();
  }

  openFullReport() {
    if (this.patientId) {
      if (this.doctorId) {
        this.router.navigate(['/doctors', this.doctorId, 'patients', this.patientId, 'reports', 'create']);
      } else {
        this.router.navigate(['/b2c/patients', this.patientId, 'reports', 'create']);
      }
    }
  }

  saveDraft() {
    this.createReportForRequest(this.requestId!);
  }

  createReportForRequest(requestId: string) {
    const effectiveCreatedBy = this.doctorId || this.loginService.getUser()?.id;
    const data = {
      request_id: requestId,
      created_by: effectiveCreatedBy,
      data: {
        ...this.reportForm.value,
      }
    };
    this.reportService.createReport(data)
      .subscribe((data: any) => {
        this.reportService.fetchReportRequests()
          .subscribe((reports: any) => {
            this.reportService.markReportRequestInProgress(requestId).subscribe();
            this.reportService.updateRequestsList(reports);
            this.location.back();
          });
      });
  }
}
